import { Icon, Tooltip, typography } from '@joor/design-system'
import sortBy from 'lodash/sortBy'
import styled from 'styled-components'

import { SocialMediaType, TradeShowAward } from '__generated__/atlas-types'

import { getURLFromString } from 'utils/getURLFromString'

import {
  getSocialMediaHref,
  getSocialMediaIcon,
} from 'features/LeadGen/RetailerProfile/retailerProfileLinks'
import { RetailerProfile } from 'features/LeadGen/RetailerProfile/retailerProfilePage.types'

const NewSectionHeader = styled.div`
  width: 100%;
  ${typography.alpha.primaryActive.headline4}
  line-height: 32px;
  letter-spacing: 1.2px;
  color: var(--color-primary-700);
  border-bottom: 1px solid var(--color-primary-300);
  padding: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledAwardBadge = styled.img`
  width: 88px;
  height: 88px;
`

const StyledLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  align-items: center;
  ${typography.alpha.primaryActive.body3}
  color: var(--color-secondary-800) !important;
  min-width: 0;
`

const StyledLinkText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const AwardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 5px;
  align-items: center;
  justify-content: left;
  gap: 10px;
  margin: 10px 0 20px 0;
`
const SocialMediaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  gap: 5px;
`

const JOOR_100_TOOLTIP =
  'The JOOR 100 is our annual list celebrating notable independent retailers from around the world.'

const SOCIAL_MEDIA_ORDER = [
  SocialMediaType.INSTAGRAM,
  SocialMediaType.TWITTER,
  SocialMediaType.FACEBOOK,
]

type BasicInfoViewModeProps = {
  website?: string
  socialMedia?: RetailerProfile['socialMediaAccounts']
  awards?: TradeShowAward[] | null
}

const BasicInfoViewMode = ({
  website,
  socialMedia,
  awards,
}: BasicInfoViewModeProps) => {
  const websiteUrl = website ? getURLFromString(website) : undefined

  const hasSocialMedia = socialMedia?.find((sm) => sm.url)
  const hasLinks = websiteUrl || hasSocialMedia

  return (
    <div>
      <div>
        {Boolean(awards?.length) && (
          <div>
            <NewSectionHeader>
              <span>Awards</span>
            </NewSectionHeader>
            <AwardsWrapper>
              {awards?.map((award) => (
                <Tooltip key={award.id} content={JOOR_100_TOOLTIP}>
                  <StyledAwardBadge
                    data-testid={`award-badge-${award.id}`}
                    src={award.awardBadgeUrl!}
                  />
                </Tooltip>
              ))}
            </AwardsWrapper>
          </div>
        )}
        {hasLinks && (
          <div>
            <NewSectionHeader>
              <span>Website & Social</span>
            </NewSectionHeader>
            <SocialMediaWrapper>
              {websiteUrl && (
                <StyledLink
                  href={websiteUrl.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`retailer-website`}
                  className="notranslate"
                >
                  <Icon iconName="global" plain variant={'ACTIVE'} />
                  <StyledLinkText>{website}</StyledLinkText>
                </StyledLink>
              )}
              {Boolean(socialMedia?.length) && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                  }}
                >
                  {sortBy(socialMedia, (media) =>
                    SOCIAL_MEDIA_ORDER.findIndex((o) => media.type === o),
                  )?.map(({ url, type }, index) => {
                    const href = getSocialMediaHref(type, url)
                    if (url === '' || !href) {
                      return null
                    }
                    return (
                      <StyledLink
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`retailer-${type}-account`}
                        key={`${index}_${url}`}
                        className="notranslate"
                      >
                        <Icon
                          iconName={
                            getSocialMediaIcon(type) as
                              | 'instagram'
                              | 'facebook'
                              | 'x'
                          }
                          plain
                          variant={'ACTIVE'}
                        />
                        <StyledLinkText>
                          {type === SocialMediaType.FACEBOOK ? href : url}
                        </StyledLinkText>
                      </StyledLink>
                    )
                  })}
                </div>
              )}
            </SocialMediaWrapper>
          </div>
        )}
      </div>
    </div>
  )
}

export default BasicInfoViewMode
